import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { useProfileVersion, getProfileVersionHash } from "./useProfileVersion";
import { invokeEdgeFunction } from "@/lib/edgeFunctionClient";
import { logError } from "@/lib/logger";
import { getMatchesFromStorage } from "@/lib/dataPrefetcher";

export interface MatchedJob {
  id: string;
  title: string;
  company: string;
  location: string | null;
  country_code?: string | null;
  location_status?: string | null;
  description: string | null;
  remote: boolean | null;
  work_mode: string | null;
  job_type: string | null;
  tags: string[] | null;
  url: string;
  source?: string | null;
  created_at: string | null;
  published_at: string | null;
  apply_method: string | null;
  apply_url: string | null;
  auto_apply_ready?: boolean | null;
  auto_apply_platform?: string | null;
  first_seen_at?: string | null;
  matchScore: number;
  matchReasons: string[];
  /** MATCH-P0-4: sub-scores for "Varför fick jag detta jobb?" (role/skills/location/recency 0..1) */
  matchSubScores?: { roleMatch: number; skillsMatch: number; locationMatch: number; recency: number } | null;
  /** MATCH-P0-6: Which hard filters let this job through (e.g. Språk, Plats, Roll) */
  passedHardFilters?: string[] | null;
  /** When backend provides missing-info (e.g. from calculate-matches), use for "Saknas" instead of generic fallback */
  missingInfo?: string[];
}

interface MatchesResponse {
  matches: MatchedJob[];
  jobsAnalyzed: number;
  totalMatches: number;
  hasMore?: boolean;
  offset?: number;
  limit?: number;
  cached?: boolean;
  /** P0-MATCH-2: when matches were generated (ISO string). If older than 24h, trigger refresh or show "Uppdaterar matchningar…" */
  generated_at?: string;
}

type UseDailyMatchesOptions = {
  /**
   * Allow consumers (e.g. Matches page) to keep this hook idle until fallback is actually needed.
   * Default true keeps existing behavior for other callers.
   */
  enabled?: boolean;
};

export const useDailyMatches = (options: UseDailyMatchesOptions = {}) => {
  const consumerEnabled = options.enabled ?? true;
  const queryClient = useQueryClient();
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [userId, setUserId] = useState<string | null>(null);
  const [allMatches, setAllMatches] = useState<MatchedJob[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastCached, setLastCached] = useState<boolean | null>(null);

  // P0.1: Get profile version for cache key - ensures fresh matches after profile changes
  const { data: profileVersion } = useProfileVersion();
  const versionHash = getProfileVersionHash(profileVersion || null);
  const queryEnabled = consumerEnabled && !!userId;

  // Get userId on mount and listen for auth changes
  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserId(session?.user?.id || null);
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const newUserId = session?.user?.id || null;
      const oldUserId = userId;
      setUserId(newUserId);
      
      // Clear cache when user changes
      if (oldUserId && newUserId !== oldUserId) {
        queryClient.removeQueries({ queryKey: ["daily-matches"] });
        setAllMatches([]);
        setHasMore(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient, userId]);

  // P0.1: Include versionHash in query key so matches refresh when profile changes
  const query = useQuery({
    queryKey: ["daily-matches", userId, versionHash],
    queryFn: async (): Promise<MatchesResponse> => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        return { matches: [], jobsAnalyzed: 0, totalMatches: 0, hasMore: false };
      }
      
      // Use robust edge function client with timeout, retry, and error handling
      const response = await invokeEdgeFunction<{
        matches: MatchedJob[];
        jobsAnalyzed: number;
        totalMatches: number;
        hasMore: boolean;
        offset: number;
        limit: number;
        cached?: boolean;
        generated_at?: string;
      }>("calculate-matches", {
        body: { 
          limit: 25, // Initial batch; backend caps free at 50 total, Pro at 100
          offset: 0,
          profileVersion: profileVersion || null,
          detailLevel: "list",
        },
        timeout: 20000, // Fail faster so cached/localStorage data shows sooner
        maxRetries: 1,
      });

      if (!response.ok || response.error) {
        const error = response.error || { message: "Kunde inte hämta matchningar", code: "UNKNOWN", retryable: false };
        logError(`[DailyMatches] Error fetching matches [${response.correlationId}]`, error);
        throw new Error(error.message);
      }

      const responseData = response.data!;
      const matches = responseData.matches || [];
      setLastRefresh(new Date());
      setAllMatches(matches);
      setHasMore(responseData.hasMore || false);
      setLastCached(responseData.cached === true);
      
      return {
        matches,
        jobsAnalyzed: responseData.jobsAnalyzed || 0,
        totalMatches: responseData.totalMatches || 0,
        hasMore: responseData.hasMore || false,
        offset: responseData.offset || 0,
        limit: responseData.limit || 15,
        cached: responseData.cached,
        generated_at: responseData.generated_at,
      };
    },
    enabled: queryEnabled,
    // PERFORMANCE: Cache for 24 hours to reduce edge function calls
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    gcTime: 1000 * 60 * 60 * 48, // Keep in garbage collection for 48 hours
    retry: 1, // Reduced from 2 to 1 for faster failure
    retryDelay: 1000, // 1 second delay between retries
    refetchOnWindowFocus: false,
    refetchOnMount: false, // PERFORMANCE: Don't refetch on mount if we have cached data
    refetchInterval: false,
    refetchIntervalInBackground: false,
    // Use cached data immediately for fast first paint (0.3s target).
    // This ensures UI shows immediately with cached matches while fresh data loads
    placeholderData: (previousData) => previousData,
    // CRITICAL: Return cached data immediately if available, even if stale
    // This frontloads content for instant perceived performance
    initialData: () => {
      // Try to get cached data from queryClient for instant render
      const cached = queryClient.getQueryData<MatchesResponse>(["daily-matches", userId, versionHash]);
      if (cached) return cached;
      
      // INSTANT LOAD: Fall back to localStorage cache for instant render after browser restart
      if (userId) {
        const storedMatches = getMatchesFromStorage(userId);
        if (storedMatches && storedMatches.length > 0) {
          return {
            matches: storedMatches as MatchedJob[],
            jobsAnalyzed: 0,
            totalMatches: storedMatches.length,
            hasMore: true, // Assume more to trigger background refresh
            offset: 0,
            limit: storedMatches.length,
            cached: true,
          };
        }
      }
      return undefined;
    },
  });

  // Load more matches (pagination)
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    setIsLoadingMore(true);
    try {
      const nextOffset = allMatches.length;
      
      // P0-1: Send profile version for validation
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      const { data: currentProfileVersion } = await supabase
        .rpc("get_profile_version", { p_user_id: currentSession?.user.id || "" });
      
      // Use robust edge function client
      const response = await invokeEdgeFunction<{
        matches: MatchedJob[];
        hasMore: boolean;
      }>("calculate-matches", {
        body: { 
          limit: 15, 
          offset: nextOffset,
          profileVersion: currentProfileVersion || null,
          detailLevel: "list",
        },
        timeout: 30000,
        maxRetries: 1,
      });

      if (!response.ok || response.error) {
        const error = response.error || { message: "Kunde inte hämta fler matchningar", code: "UNKNOWN", retryable: false };
        logError(`[DailyMatches] Error loading more [${response.correlationId}]`, error);
        return;
      }
      
      const responseData = response.data!;

      const newMatches = responseData.matches || [];
      setAllMatches(prev => [...prev, ...newMatches]);
      setHasMore(responseData.hasMore || false);
    } catch (error) {
      logError("[DailyMatches] Error in loadMore", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, allMatches.length]);

  // Refresh on new day (midnight crossing) when user returns to tab
  useEffect(() => {
    if (!queryEnabled) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && userId) {
        const now = new Date();
        const lastRefreshDate = lastRefresh.toDateString();
        const todayDate = now.toDateString();

        if (lastRefreshDate !== todayDate) {
          setAllMatches([]);
          setHasMore(false);
          query.refetch();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [query, lastRefresh, userId, queryEnabled]);

  // Also refresh at local midnight even if the tab stays open
  useEffect(() => {
    if (!queryEnabled) return;
    if (!userId) return;

    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setHours(24, 0, 5, 0);

    const msUntilMidnight = nextMidnight.getTime() - now.getTime();
    if (msUntilMidnight <= 0) return;

    const timeoutId = window.setTimeout(() => {
      if (document.visibilityState === "visible") {
        const lastRefreshDate = lastRefresh.toDateString();
        const todayDate = new Date().toDateString();

        if (lastRefreshDate !== todayDate) {
          setAllMatches([]);
          setHasMore(false);
          query.refetch();
        }
      }
    }, msUntilMidnight);

    return () => window.clearTimeout(timeoutId);
  }, [query, userId, lastRefresh, queryEnabled]);

  // Helper to instantly remove a job from cache (optimistic update)
  const removeJobFromCache = useCallback((jobId: string) => {
    setAllMatches(prev => prev.filter(job => job.id !== jobId));
    queryClient.setQueryData<MatchesResponse>(["daily-matches", userId, versionHash], (old) => {
      if (!old) return old;
      return {
        ...old,
        matches: old.matches.filter(job => job.id !== jobId),
        totalMatches: old.totalMatches - 1,
      };
    });
  }, [queryClient, userId, versionHash]);

  // Manual refresh function
  const refreshMatches = useCallback(() => {
    setAllMatches([]);
    setHasMore(false);
    return query.refetch();
  }, [query]);

  // P0-MATCH-2: generated_at and stale check (older than 24h → trigger refresh or show "Uppdaterar matchningar…")
  const generatedAt = query.data?.generated_at;
  const STALE_MS = 24 * 60 * 60 * 1000;
  const isStale = !!generatedAt && (Date.now() - new Date(generatedAt).getTime() > STALE_MS);

  // P0-MATCH-2: When matches are older than 24h, refetch once in background (ref avoids loop)
  const staleRefetchDone = useRef(false);
  useEffect(() => {
    if (!queryEnabled) return;
    if (!userId || !isStale || query.isFetching || staleRefetchDone.current) return;
    staleRefetchDone.current = true;
    query.refetch().finally(() => { staleRefetchDone.current = false; });
  }, [userId, isStale, queryEnabled, query]);

  // FAANG: When cached match count is very low (e.g. 2), refetch once so user gets fresh list with backend fallbacks
  const fewMatchesRefetchDone = useRef(false);
  const cachedCount = allMatches.length || (query.data?.totalMatches ?? 0);
  useEffect(() => {
    if (!queryEnabled) return;
    if (!userId || cachedCount > 3 || query.isFetching || fewMatchesRefetchDone.current) return;
    fewMatchesRefetchDone.current = true;
    void query.refetch();
  }, [userId, cachedCount, queryEnabled, query]);

  // INSTANT LOAD FIX: Report loading until we have a terminal state so UI shows skeleton
  // from first paint instead of wrong text ("Inget nytt just nu" / "Aktivera mäklaragenten").
  // When userId just became truthy, query.isLoading can be false for one frame before fetch starts,
  // so also treat "userId set but no data and no error yet" as loading.
  const hasSettled = query.data !== undefined || query.isError;
  const isLoading = !queryEnabled ? false : (!userId || query.isLoading || (!!userId && !hasSettled));

  return {
    ...query,
    isLoading,
    dailyMatches: allMatches.length > 0 ? allMatches : (query.data?.matches || []),
    jobsAnalyzed: query.data?.jobsAnalyzed || 0,
    totalMatches: query.data?.totalMatches || 0,
    cached: lastCached ?? query.data?.cached,
    generated_at: generatedAt,
    isStale,
    hasMore,
    isLoadingMore,
    loadMore,
    removeJobFromCache,
    refreshMatches,
    lastRefresh,
    // Expose error state for better error handling
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
